import React, { useState, useEffect } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { createNotificationTemplateSchema } from '@shared/schemas/notificationTemplate'
import { NotificationTemplate } from '@shared/types/notificationTemplate'
import { useNotificationTemplates } from '@/hooks/useNotificationTemplates'
import { X, Save, Eye, Info } from 'lucide-react'
import FormField from '@/components/ui/FormField'
import { useToast } from '@/hooks/useToast'

interface NotificationTemplateEditorProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  template?: NotificationTemplate | null
  initialTemplateName?: string
}

export const NotificationTemplateEditor: React.FC<NotificationTemplateEditorProps> = ({
  isOpen,
  onClose,
  onSuccess,
  template,
  initialTemplateName
}) => {
  const { createTemplate: doCreateTemplate, templateMetadata, loading } = useNotificationTemplates()
  const { toast } = useToast()
  const [selectedTemplateName, setSelectedTemplateName] = useState<string>('')
  const [showVariables, setShowVariables] = useState(false)

  const defaultName = template?.name || initialTemplateName || ''

  const methods = useForm({
    resolver: zodResolver(createNotificationTemplateSchema),
    defaultValues: {
      name: defaultName,
      type: 'EMAIL',
      subject: template?.subject || '',
      bodyHtml: template?.bodyHtml || '',
      bodyText: template?.bodyText || '',
      variables: Array.isArray(template?.variables) ? template.variables : [],
      description: template?.description || ''
    }
  })

  const { handleSubmit, reset, watch, setValue, formState } = methods
  const watchedName = watch('name')
  const { errors } = formState

  useEffect(() => {
    if (watchedName) {
      setSelectedTemplateName(watchedName)
    }
  }, [watchedName])

  // Reset form when template or initialTemplateName changes
  useEffect(() => {
    if (isOpen) {
      const formData = {
        name: template?.name || initialTemplateName || '',
        type: 'EMAIL',
        subject: template?.subject || '',
        bodyHtml: template?.bodyHtml || '',
        bodyText: template?.bodyText || '',
        variables: Array.isArray(template?.variables) ? template.variables : [],
        description: template?.description || ''
      }

      reset(formData)

      // Explicitly set the name field to ensure the dropdown is populated
      if (initialTemplateName) {
        setValue('name', initialTemplateName)
      }
    }
  }, [template, initialTemplateName, isOpen, reset, setValue])

  const extractVariablesFromTemplate = (text: string): string[] => {
    // Extract all {{variableName}} patterns
    const regex = /\{\{(\w+)\}\}/g
    const matches = new Set<string>()
    let match
    while ((match = regex.exec(text)) !== null) {
      matches.add(match[1])
    }
    return Array.from(matches)
  }

  const onSubmit = async (data: any) => {
    try {
      // Auto-extract variables from template content
      const extractedVariables = [
        ...extractVariablesFromTemplate(data.subject),
        ...extractVariablesFromTemplate(data.bodyHtml),
        ...extractVariablesFromTemplate(data.bodyText)
      ]
      const uniqueVariables = Array.from(new Set(extractedVariables))

      const submitData = {
        ...data,
        variables: uniqueVariables
      }

      const result = await doCreateTemplate(submitData)
      toast.success('Template Created', 'New template version created successfully')
      reset()
      onSuccess()
    } catch (err) {
      toast.error('Creation Failed', `Failed to create template: ${err instanceof Error ? err.message : String(err)}`)
    }
  }

  const getAvailableVariables = () => {
    if (!selectedTemplateName || !templateMetadata) return []
    return templateMetadata[selectedTemplateName]?.availableVariables || []
  }

  const insertVariable = (varName: string, field: 'subject' | 'bodyHtml' | 'bodyText') => {
    const currentValue = watch(field)
    setValue(field, `${currentValue}{{${varName}}}`)
  }

  if (!isOpen) return null

  const templateNames = templateMetadata ? Object.keys(templateMetadata) : []
  const availableVariables = getAvailableVariables()


  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg border border-border w-full max-w-5xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-foreground">
            {template ? 'Create New Version' : 'Create Notification Template'}
          </h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)} className="p-6">
            <div className="grid grid-cols-3 gap-6">
              {/* Main Form - 2 columns */}
              <div className="col-span-2 space-y-4">
                <div className="space-y-2">
                  <label htmlFor="name-field" className="block text-sm font-medium text-foreground">
                    Template Name <span className="text-destructive ml-1">*</span>
                  </label>
                  <select
                    id="name-field"
                    disabled={loading}
                    value={watchedName || ''}
                    onChange={(e) => {
                      console.log('📝 Dropdown changed to:', e.target.value)
                      setValue('name', e.target.value)
                    }}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary ${
                      errors.name ? 'border-destructive' : 'border-border'
                    }`}
                  >
                    <option value="">Select a template type...</option>
                    {templateNames.map(name => (
                      <option key={name} value={name}>
                        {templateMetadata?.[name]?.displayName || name}
                      </option>
                    ))}
                  </select>
                  {errors.name && (
                    <p className="text-sm text-destructive mt-1">{String(errors.name.message)}</p>
                  )}
                </div>

                <FormField
                  name="subject"
                  label="Email Subject"
                  placeholder="Enter email subject line"
                  required
                  disabled={loading}
                  helperText="Use {{variableName}} for dynamic content"
                />

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    HTML Body <span className="text-destructive">*</span>
                  </label>
                  <textarea
                    {...methods.register('bodyHtml')}
                    rows={12}
                    className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Enter HTML template..."
                    disabled={loading}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Use {`{{variableName}}`} for dynamic content. HTML tags are supported.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Plain Text Body <span className="text-destructive">*</span>
                  </label>
                  <textarea
                    {...methods.register('bodyText')}
                    rows={8}
                    className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Enter plain text template..."
                    disabled={loading}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Plain text fallback for email clients that don't support HTML
                  </p>
                </div>

                <FormField
                  name="description"
                  label="Description"
                  placeholder="Optional description of this version"
                  disabled={loading}
                  helperText="Describe what changed in this version"
                />
              </div>

              {/* Variables Sidebar - 1 column */}
              <div className="col-span-1">
                <div className="sticky top-6 bg-muted/30 rounded-lg p-4 border border-border">
                  <div className="flex items-center gap-2 mb-3">
                    <Info className="h-4 w-4 text-primary" />
                    <h3 className="font-semibold text-foreground">Available Variables</h3>
                  </div>

                  {availableVariables.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      Select a template name to see available variables
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {availableVariables.map((variable) => (
                        <div key={variable.name} className="space-y-1">
                          <div className="flex items-start justify-between gap-2">
                            <code className="text-xs font-mono bg-background px-2 py-1 rounded border border-border">
                              {`{{${variable.name}}}`}
                            </code>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {variable.description || 'No description available'}
                          </p>
                          {variable.example && (
                            <p className="text-xs text-muted-foreground/70">
                              Example: {variable.example}
                            </p>
                          )}
                        </div>
                      ))}

                      <div className="pt-3 border-t border-border">
                        <p className="text-xs text-muted-foreground mb-2">
                          <strong>Usage:</strong> Type or copy variables into your template
                        </p>
                        <code className="text-xs font-mono bg-background px-2 py-1 rounded block">
                          Hello {`{{employeeName}}`}
                        </code>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-6 border-t border-border mt-6">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="px-4 py-2 border border-border rounded-lg hover:bg-muted transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                <Save className="h-4 w-4" />
                {loading ? 'Creating...' : 'Create Template'}
              </button>
            </div>
          </form>
        </FormProvider>
      </div>
    </div>
  )
}
